import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import { fr } from "date-fns/locale";

export const agentOutdated = (v, latest) => {
  if (!latest) return false;
  const a = String(v || "0").split(".").map(Number), b = String(latest).split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) < (b[i] || 0);
  }
  return false;
};

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

export const api = axios.create({ baseURL: API, withCredentials: true });

// Refresh the short-lived access cookie once on 401, then retry
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    const skip = ["/auth/login", "/auth/register", "/auth/refresh"].some((p) => cfg?.url?.includes(p));
    if (error.response?.status !== 401 || cfg._retry || skip) throw error;
    cfg._retry = true;
    if (!refreshing) {
      // Swallow the refresh rejection here so the shared promise never surfaces as an
      // unhandled rejection; callers below decide what to do via the boolean result.
      refreshing = api.post("/auth/refresh").then(() => true).catch(() => false).finally(() => { refreshing = null; });
    }
    const ok = await refreshing;
    if (!ok) throw error;
    return api(cfg);
  },
);

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Erreur";
};

export const timeAgo = (iso) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr }) : "—");
export const fmtDate = (iso) => (iso ? format(new Date(iso), "dd/MM/yyyy HH:mm", { locale: fr }) : "—");
export const fmtIdle = (s) => {
  if (!s) return "0m";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}j ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};
export const since = (iso) => (iso ? fmtIdle((Date.now() - new Date(iso).getTime()) / 1000) : "");
// Resolves true/false and never rejects: the clipboard API throws when the page is not focused or not allowed.
export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    ta.remove();
    return ok;
  }
};
export const copyOrWarn = (text, msg = "Copié") => copyText(text).then((ok) => (ok ? toast.success(msg) : toast.error("Copie impossible : sélectionnez le texte et copiez-le à la main")));
export const fmtSize = (b) => (b < 1024 ? `${b} o` : b < 1048576 ? `${(b / 1024).toFixed(0)} Ko` : `${(b / 1048576).toFixed(1)} Mo`);
export const fmtDuration = (s) => {
  if (s == null) return "en cours";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}h ${m}min` : m ? `${m}min ${sec}s` : `${sec}s`;
};
